import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { listAgents } from '../lib/api';
import { API_BASE } from '../lib/env';
import { agentFromPublicProfile } from '../lib/agents';
import type { Agent } from '../types';
import { useAuth } from './AuthContext';

type AgentsContextValue = {
  agents: Agent[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  getAgent: (id: string | undefined) => Agent | undefined;
  canManageAgents: boolean;
  sessionUserId: string | null;
};

const AgentsContext = createContext<AgentsContextValue | null>(null);

export function AgentsProvider({ children }: { children: React.ReactNode }) {
  const { status, token } = useAuth();
  const [agents, setAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [canManageAgents, setCanManageAgents] = useState(false);
  const [sessionUserId, setSessionUserId] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (status !== 'authenticated' || !token) {
      setAgents([]);
      setLoading(false);
      setCanManageAgents(false);
      setSessionUserId(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const list = await listAgents();
      setAgents(list.map(agentFromPublicProfile));
      const sessionRes = await fetch(`${API_BASE}/api/auth/session`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const session = (await sessionRes.json().catch(() => ({}))) as {
        kind?: string;
        userId?: string;
      };
      setCanManageAgents(session.kind === 'env_admin');
      setSessionUserId(typeof session.userId === 'string' ? session.userId : null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load agents');
      setAgents([]);
    } finally {
      setLoading(false);
    }
  }, [status, token]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const getAgent = useCallback(
    (id: string | undefined) => {
      if (!id) return agents[0];
      return agents.find((a) => a.id === id) ?? agents[0];
    },
    [agents],
  );

  const value = useMemo(
    () => ({ agents, loading, error, refresh, getAgent, canManageAgents, sessionUserId }),
    [agents, loading, error, refresh, getAgent, canManageAgents, sessionUserId],
  );

  return <AgentsContext.Provider value={value}>{children}</AgentsContext.Provider>;
}

export function useAgents(): AgentsContextValue {
  const ctx = useContext(AgentsContext);
  if (!ctx) throw new Error('useAgents must be used within AgentsProvider');
  return ctx;
}

/** Resolve agent for a route param; returns undefined while loading if list empty. */
export function useAgent(agentId: string | undefined): Agent | undefined {
  const { getAgent, agents, loading } = useAgents();
  if (loading && agents.length === 0) return undefined;
  return getAgent(agentId);
}
